import { type AtendenteDTO, type ConversaDetalheDTO, type MensagemDTO } from '@viniko/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  Clock,
  FileText,
  Image,
  Lock,
  Mic,
  Paperclip,
  Send,
  Smile,
  Trash2,
  X,
} from 'lucide-react';
import { EVENTO_COLAR } from '../../lib/cotacao';
import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { useToast } from '../../componentes/ui';
import { enviarArquivo, post } from '../../lib/api';
import { chaves, useJanelaAberta, useRespostasRapidas } from '../../lib/consultas';
import { envolverSelecao } from '../../lib/formatacao';
import { ModalModelo } from './ModalModelo';

const ATALHOS_FORMATO: Record<string, string> = { b: '*', i: '_', 'shift+x': '~' };

const EMOJIS = [
  '😀',
  '😁',
  '😂',
  '😊',
  '😉',
  '😍',
  '😘',
  '🙂',
  '🤔',
  '😅',
  '😎',
  '🥰',
  '👍',
  '👌',
  '🙏',
  '👏',
  '💪',
  '🤝',
  '👋',
  '✌️',
  '☝️',
  '👉',
  '🙌',
  '✅',
  '❤️',
  '💙',
  '🔥',
  '⭐',
  '🎉',
  '📦',
  '🚚',
  '📄',
  '📎',
  '🖨️',
  '🎨',
  '💰',
  '⏰',
  '📅',
  '📍',
  '📞',
  '💬',
  '❗',
  '❓',
  '⚠️',
  '🆗',
  '🆕',
  '😢',
  '😬',
];

interface Props {
  conversa: ConversaDetalheDTO;
  eu: AtendenteDTO;
  respondendo: MensagemDTO | null;
  cancelarResposta: () => void;
  anexar: (arquivos: File[]) => void;
}

export function Compositor({ conversa: c, eu, respondendo, cancelarResposta, anexar }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const respostas = useRespostasRapidas().data ?? [];
  const minha = c.status === 'atendimento' && c.atendente?.id === eu.id;
  const janelaAbertaAgora = useJanelaAberta();
  const janela = janelaAbertaAgora(c.ultimaMensagemClienteEm);

  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [menuAnexo, setMenuAnexo] = useState(false);
  const [emojis, setEmojis] = useState(false);
  const [modeloAberto, setModeloAberto] = useState(false);
  const ta = useRef<HTMLTextAreaElement>(null);
  const inputFotos = useRef<HTMLInputElement>(null);
  const inputDocs = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (respondendo) ta.current?.focus();
  }, [respondendo]);

  useEffect(() => {
    const aoColar = (e: Event) => {
      const { conversaId, texto: novo } = (e as CustomEvent<{ conversaId: string; texto: string }>)
        .detail;
      if (conversaId !== c.id) return;
      setTexto((v) => (v.trim() ? `${v.trimEnd()}\n\n${novo}` : novo));
      requestAnimationFrame(() => ta.current?.focus());
    };
    window.addEventListener(EVENTO_COLAR, aoColar);
    return () => window.removeEventListener(EVENTO_COLAR, aoColar);
  }, [c.id]);

  const termoRapida = /^\/(\S*)$/.exec(texto)?.[1];
  const rapidas =
    termoRapida === undefined
      ? []
      : respostas.filter((r) => r.atalho.startsWith(termoRapida.toLowerCase())).slice(0, 6);

  function inserir(t: string) {
    const el = ta.current;
    if (!el) return setTexto((v) => v + t);
    const ini = el.selectionStart ?? texto.length;
    const fim = el.selectionEnd ?? texto.length;
    setTexto(texto.slice(0, ini) + t + texto.slice(fim));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(ini + t.length, ini + t.length);
    });
  }

  async function enviar() {
    const t = texto.trim();
    if (!t || enviando) return;
    setEnviando(true);
    try {
      const m = await post<MensagemDTO>(`/api/conversas/${c.id}/mensagens`, {
        texto: t,
        respondeAId: respondendo?.id,
      });
      qc.setQueryData<ConversaDetalheDTO>(chaves.conversa(c.id), (d) =>
        d && !d.mensagens.some((x) => x.id === m.id) ? { ...d, mensagens: [...d.mensagens, m] } : d,
      );
      setTexto('');
      cancelarResposta();
    } catch (e) {
      toast((e as Error).message, 'erro');
    } finally {
      setEnviando(false);
      ta.current?.focus();
    }
  }

  function teclas(e: KeyboardEvent<HTMLTextAreaElement>) {
    const atalho =
      e.ctrlKey || e.metaKey
        ? ATALHOS_FORMATO[(e.shiftKey ? 'shift+' : '') + e.key.toLowerCase()]
        : undefined;
    if (atalho) {
      e.preventDefault();
      envolverSelecao(e.currentTarget, atalho, (novo, a, b) => {
        setTexto(novo);
        requestAnimationFrame(() => ta.current?.setSelectionRange(a, b));
      });
      return;
    }
    if (rapidas.length && (e.key === 'Enter' || e.key === 'Tab')) {
      e.preventDefault();
      setTexto(rapidas[0]!.texto);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void enviar();
    }
  }

  function colar(e: ClipboardEvent<HTMLTextAreaElement>) {
    const arquivos = [...e.clipboardData.files];
    if (arquivos.length) {
      e.preventDefault();
      anexar(arquivos);
    }
  }

  const bloqueio = motivoBloqueio(c, minha, janela);
  if (bloqueio) {
    const podeModelo = minha && !janela && !c.contato.bloqueado;
    return (
      <div className="compositor">
        <div className="bloqueio">
          {bloqueio.icone} {bloqueio.texto}
          {podeModelo && (
            <button className="btn primario" onClick={() => setModeloAberto(true)}>
              Enviar modelo
            </button>
          )}
        </div>
        {modeloAberto && <ModalModelo conversaId={c.id} fechar={() => setModeloAberto(false)} />}
      </div>
    );
  }

  return (
    <div className="compositor">
      {respondendo && (
        <div className="respondendo">
          <span>
            <b>
              {respondendo.autor === 'cliente'
                ? c.contato.nome
                : (respondendo.atendente?.nomeExibicao ?? 'IA')}
            </b>
            {respondendo.conteudo || respondendo.midiaNome || 'Mídia'}
          </span>
          <button
            className="icone-btn sem-borda"
            aria-label="Cancelar resposta"
            onClick={cancelarResposta}
          >
            <X />
          </button>
        </div>
      )}

      {rapidas.length > 0 && (
        <div className="flutuante rapidas entra" role="listbox" aria-label="Respostas rápidas">
          {rapidas.map((r) => (
            <button
              key={r.id}
              role="option"
              aria-selected={false}
              onClick={() => (setTexto(r.texto), ta.current?.focus())}
            >
              <b>/{r.atalho}</b> {r.texto}
            </button>
          ))}
        </div>
      )}

      {emojis && (
        <div className="flutuante emojis entra" role="dialog" aria-label="Emojis">
          {EMOJIS.map((e) => (
            <button key={e} onClick={() => inserir(e)} aria-label={e}>
              {e}
            </button>
          ))}
        </div>
      )}

      {menuAnexo && (
        <div className="flutuante menu-anexo entra" role="menu">
          <button role="menuitem" onClick={() => (setMenuAnexo(false), inputDocs.current?.click())}>
            <span className="ic doc">
              <FileText />
            </span>
            Documento
          </button>
          <button
            role="menuitem"
            onClick={() => (setMenuAnexo(false), inputFotos.current?.click())}
          >
            <span className="ic foto">
              <Image />
            </span>
            Fotos e vídeos
          </button>
        </div>
      )}

      <div className="barra">
        <button
          className="icone-btn sem-borda"
          aria-label="Anexar"
          aria-expanded={menuAnexo}
          onClick={() => (setMenuAnexo((v) => !v), setEmojis(false))}
        >
          <Paperclip />
        </button>
        <button
          className="icone-btn sem-borda"
          aria-label="Emojis"
          aria-expanded={emojis}
          onClick={() => (setEmojis((v) => !v), setMenuAnexo(false))}
        >
          <Smile />
        </button>
        <textarea
          id="compositor-texto"
          ref={ta}
          rows={1}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={teclas}
          onPaste={colar}
          onFocus={() => (setEmojis(false), setMenuAnexo(false))}
          placeholder="Digite uma mensagem"
          style={{ height: Math.min(140, 42 + (texto.split('\n').length - 1) * 20) }}
        />
        {texto.trim() ? (
          <button
            className="enviar"
            aria-label="Enviar"
            disabled={enviando}
            onClick={() => void enviar()}
          >
            <Send className="ico-enviar" />
          </button>
        ) : (
          <Gravador conversaId={c.id} respondeAId={respondendo?.id} aoEnviar={cancelarResposta} />
        )}
      </div>

      <input
        ref={inputFotos}
        type="file"
        accept="image/jpeg,image/png,image/webp,video/mp4,video/3gpp"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) anexar([...e.target.files]);
          e.target.value = '';
        }}
      />
      <input
        ref={inputDocs}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) anexar([...e.target.files]);
          e.target.value = '';
        }}
      />
    </div>
  );
}

function Gravador({
  conversaId,
  respondeAId,
  aoEnviar,
}: {
  conversaId: string;
  respondeAId?: string;
  aoEnviar: () => void;
}) {
  const toast = useToast();
  const [gravando, setGravando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const rec = useRef<MediaRecorder | null>(null);
  const partes = useRef<Blob[]>([]);
  const descartar = useRef(false);

  useEffect(() => {
    if (!gravando) return;
    const t = setInterval(() => setSegundos((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [gravando]);

  async function iniciar() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const tipo = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm'].find((t) =>
        MediaRecorder.isTypeSupported(t),
      );
      const r = new MediaRecorder(stream, tipo ? { mimeType: tipo } : undefined);
      partes.current = [];
      descartar.current = false;
      r.ondataavailable = (e) => {
        if (e.data.size) partes.current.push(e.data);
      };
      r.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setGravando(false);
        if (descartar.current || !partes.current.length) return;
        const mime = (r.mimeType || 'audio/webm').split(';')[0]!;
        const blob = new Blob(partes.current, { type: mime });
        try {
          await enviarArquivo(conversaId, blob, {
            nome: `audio.${mime.includes('ogg') ? 'ogg' : 'webm'}`,
            respondeAId,
          });
          aoEnviar();
        } catch (e) {
          toast((e as Error).message, 'erro');
        }
      };
      rec.current = r;
      r.start();
      setSegundos(0);
      setGravando(true);
    } catch {
      toast('Não consegui acessar o microfone. Libere a permissão no navegador.', 'erro');
    }
  }

  const parar = (cancelar: boolean) => {
    descartar.current = cancelar;
    rec.current?.stop();
  };

  if (!gravando) {
    return (
      <button className="enviar" aria-label="Gravar áudio" onClick={iniciar}>
        <Mic />
      </button>
    );
  }
  const mmss = `${Math.floor(segundos / 60)}:${String(segundos % 60).padStart(2, '0')}`;
  return (
    <div className="gravando">
      <button
        className="icone-btn sem-borda"
        aria-label="Descartar áudio"
        onClick={() => parar(true)}
      >
        <Trash2 />
      </button>
      <span className="rec-ponto" aria-hidden />
      <span className="num">{mmss}</span>
      <button className="enviar" aria-label="Enviar áudio" onClick={() => parar(false)}>
        <Send className="ico-enviar" />
      </button>
    </div>
  );
}

function motivoBloqueio(c: ConversaDetalheDTO, minha: boolean, janela: boolean) {
  if (c.contato.bloqueado)
    return { icone: <Ban />, texto: 'Contato bloqueado. Desbloqueie pelo menu ⋮ para conversar.' };
  if (c.status === 'bot')
    return {
      icone: <Lock />,
      texto: 'A IA está atendendo. Clique em Assumir, no topo, para responder.',
    };
  if (c.status === 'fila')
    return {
      icone: <Lock />,
      texto: 'Conversa na fila. Clique em Assumir, no topo, para responder.',
    };
  if (c.status === 'finalizada')
    return { icone: <Lock />, texto: 'Conversa finalizada. Use "Reabrir" para continuar.' };
  if (!minha)
    return {
      icone: <Lock />,
      texto: `Esta conversa está com ${c.atendente?.nomeExibicao}. Peça a transferência.`,
    };
  if (!janela)
    return {
      icone: <Clock />,
      texto:
        'Janela de 24h fechada: pra falar com o cliente, envie um modelo de mensagem ou use o celular.',
    };
  return null;
}
